import React, {useCallback, useEffect, useMemo, useState} from 'react';

import {Button, Checkbox, Divider, Layout, ListItem, Select, TextField} from '@phonectas/unifon-ui-kit';
import {useTranslation} from 'react-i18next';
import CustomTextArea from 'common/components/CustomTextArea';

export interface ZendeskTicket {
    assignee_id?: number;
    description: string;
    group_id?: number | undefined;
    priority?: 'low' | 'normal' | 'high' | 'urgent' | undefined;
    requester_id: number | undefined;
    subject: string;
    tags?: string[];
    type?: 'question' | 'incident' | 'problem' | 'task';
    onClose?: () => void;
}

interface CreateZendeskContentProps {
    onClose?: () => void;
    requesterId?: number;
    assigneeId?: number;
    groupId?: number;
}

const CreateZendeskContent: React.FC<CreateZendeskContentProps> = ({
	onClose,
	requesterId,
	assigneeId,
	groupId
}) => {
	const [t] = useTranslation();
	const [simpleTicket, setSimpleTicket] = useState<boolean>(true);
	const [canSendTicket, setCanSendTicket] = useState<boolean>(false); 
	const defaultTicket: ZendeskTicket = useMemo(() => ({
		description: '',
		requester_id: requesterId || 222,
		subject: '',
		tags: [],
		type: 'question',
		priority: 'normal',
		assignee_id: assigneeId || 0,
		group_id: groupId || 0,
	}), [requesterId, assigneeId, groupId]);

	const [ticket, setTicket] = useState<ZendeskTicket>(defaultTicket);

	const tags = useMemo(() => [
		'account',
		'access',
		'password-reset',
		'billing',
		'urgent',
		'vip-customer',
		'escalation'
	], []);

	const handleTicketChange = useCallback((field: keyof ZendeskTicket, value: unknown) => {
		setTicket((prev) => ({
			...prev,
			[field]: value,
		}));
	}, []);

	const toggleTicketType = useCallback(() => {
		setSimpleTicket((prev) => !prev);
	}, []);

	useEffect(() => {
		if (simpleTicket) {
			const canSendTicket = !!(ticket.subject && ticket.description && ticket.requester_id);
			setCanSendTicket(canSendTicket);
		} else if (!simpleTicket) {
			const canSendTicket = !!(ticket.subject && ticket.description && ticket.requester_id);
			setCanSendTicket(canSendTicket);
		}

	}, [simpleTicket, ticket]);

	useEffect(() => {
		return () => {
			if (onClose) {
				setTicket(defaultTicket);
			}
		};
	}, [onClose]);

	return (
		<Layout fullWidth direction="column">
			<Divider />
			<Layout fullWidth justifyContent="end">
				<Checkbox
					label={t('Simple')}
					checked={simpleTicket}
					onChange={toggleTicketType}
					toggle
					big
				/>
			</Layout>
			<Layout fullWidth direction="row" nowrap gap="md">
				{!simpleTicket && (
					<Layout direction="column" gap="xs" style={{width: '280px'}}>
						<Select
							label={t('Ticket Type')}
							value={ticket.type}
							onChange={(value) => handleTicketChange('type', value)}
							fullWidth
						>
							{
								['question', 'incident', 'problem', 'task'].map((type) => (
									<ListItem
										key={type}
										value={type}
										primaryText={type}
									/>
								))
							}
						</Select>
						<Select
							label={t('Priority')}
							value={ticket.priority}
							onChange={(value) => handleTicketChange('priority', value)}
							fullWidth
						>
							{
								['low', 'normal', 'high', 'urgent'].map((priority) => (
									<ListItem
										key={priority}
										value={priority}
										primaryText={priority}
									/>
								))
							}
						</Select>
						<TextField
							label={t('Requester ID')}
							value={String(ticket.requester_id) || ''}
							onChange={e => handleTicketChange('requester_id', e.target.value === '' ? undefined : Number(e.target.value))}
							fullWidth
						/>
						<TextField
							label={t('Assignee ID')}
							value={String(ticket.assignee_id) || ''}
							onChange={e => handleTicketChange('assignee_id', e.target.value === '' ? undefined : Number(e.target.value))}
							fullWidth
						/>
						<TextField
							label={t('Group ID')}
							value={String(ticket.group_id) || ''}
							onChange={e => handleTicketChange('group_id', e.target.value === '' ? '' : Number(e.target.value))}
							fullWidth
						/>
					</Layout>
				)}
				<Layout fullWidth direction="column" gap="xs">
					<TextField 
						label={t('Subject')}
						value={ticket.subject}
						onChange={(e) => handleTicketChange('subject', e.target.value)}
						fullWidth
					/>
					<Select
						label={t('Tags')}
						multiple
						value={ticket.tags}
						onChange={(value) => handleTicketChange('tags', value)}
						fullWidth
					>
						{tags.map((tag) => (
							<ListItem
								key={tag}
								value={tag}
								primaryText={tag}
							/>
						))}
					</Select>
					<CustomTextArea
						label={t('Description')}
						value={ticket.description}
						name='description'
						onTextChange={(value) => handleTicketChange('description', value)}
						fullWidth
						rows={10}
					/>
				</Layout>
			</Layout>
			<Layout fullWidth direction="row" nowrap>
				<Layout fullWidth justifyContent="end">
					<Divider />
					<Button
						variant="primary"
						color="success"
						disabled={!canSendTicket}
					>
						{t('Create Ticket')}
					</Button>
				</Layout>
			</Layout>
		</Layout>
	);
};

export default CreateZendeskContent;
